import { Form } from "antd";
import {
  LOYALTY_APPLY_SCOPE,
  LOYALTY_PACKAGE_STATUS,
  LOYALTY_PROGRAM_FILTER_PAGE_SIZE,
  PACKAGE_FORM_MODE,
  TIME_INTERVAL_OPTIONS,
} from "features/LoyaltyPackage/core/constants";
import {
  useCreateLoyaltyPeriod,
  useLoyaltyCategoryOptions,
  useLoyaltyPeriod,
  useLoyaltyProgram,
  useLoyaltyPrograms,
  useUpdateLoyaltyPeriod,
} from "features/LoyaltyPackage/data/hooks";
import {
  buildPeriodInput,
  createDefaultMilestone,
  getEffectiveApplyScope,
  getEffectivePackageDateRangeFromProgram,
  hasConfiguredMilestones,
  mapPeriodProductsToForm,
  mapPeriodToFormValues,
  serializePackageFormForCompare,
  suggestTimeIntervalFromProgram,
  toFormDateRangeMoment,
  validateMilestoneFields,
  validatePackageForm,
  validatePackageProducts,
} from "features/LoyaltyPackage/core/utils";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { notify } from "utils/helperFuncs";

function useQueryParam(name) {
  return useMemo(() => new URLSearchParams(window.location.search).get(name), [name]);
}

function usePackageForm({ mode = PACKAGE_FORM_MODE.CREATE, periodId } = {}) {
  const { t } = useTranslation();
  const history = useHistory();
  const [form] = Form.useForm();

  const cloneId = useQueryParam("clone");
  const presetProgramId = useQueryParam("programId");
  const isEditMode = mode === PACKAGE_FORM_MODE.EDIT;
  const isCopyMode = mode === PACKAGE_FORM_MODE.COPY || Boolean(cloneId);
  const sourcePeriodId = isCopyMode ? cloneId : null;
  const editPeriodId = isEditMode ? periodId : null;

  const [milestones, setMilestones] = useState([createDefaultMilestone()]);
  const [milestoneErrors, setMilestoneErrors] = useState([]);
  const [products, setProducts] = useState([]);
  const [productError, setProductError] = useState(null);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [cancelVisible, setCancelVisible] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState(null);
  const [initialized, setInitialized] = useState(!isCopyMode && !isEditMode);
  const [presetProgramApplied, setPresetProgramApplied] = useState(false);
  const [productsInitialized, setProductsInitialized] = useState(false);
  const [editBaseline, setEditBaseline] = useState(null);
  const [formRevision, setFormRevision] = useState(0);
  const [watchedFields, setWatchedFields] = useState({
    loyaltyPackageId: undefined,
    applyScope: mode === PACKAGE_FORM_MODE.CREATE ? LOYALTY_APPLY_SCOPE.MULTIPLE_PRODUCTS : undefined,
    categoryId: undefined,
  });

  const syncWatchedFields = useCallback((partial) => {
    setWatchedFields((prev) => ({ ...prev, ...partial }));
  }, []);

  const bumpFormRevision = useCallback(() => {
    setFormRevision((revision) => revision + 1);
  }, []);

  useEffect(() => {
    if (mode !== PACKAGE_FORM_MODE.CREATE) return;
    form.setFieldsValue({ applyScope: LOYALTY_APPLY_SCOPE.MULTIPLE_PRODUCTS });
    syncWatchedFields({ applyScope: LOYALTY_APPLY_SCOPE.MULTIPLE_PRODUCTS });
  }, [form, mode, syncWatchedFields]);

  const { data: sourcePeriod, loading: sourceLoading } = useLoyaltyPeriod(sourcePeriodId);
  const { data: editPeriod, loading: editLoading } = useLoyaltyPeriod(editPeriodId);

  const notEditable = isEditMode && editPeriod && editPeriod.status !== LOYALTY_PACKAGE_STATUS.UPCOMING;

  const [programDropdownOpen, setProgramDropdownOpen] = useState(false);
  const [programOffset, setProgramOffset] = useState(0);
  const [accumulatedPrograms, setAccumulatedPrograms] = useState([]);

  const { data: programsPage, loading: programsLoading } = useLoyaltyPrograms(
    { isActive: true },
    { offset: programOffset, limit: LOYALTY_PROGRAM_FILTER_PAGE_SIZE },
    { skip: !programDropdownOpen }
  );
  const { data: selectedProgramFromApi } = useLoyaltyProgram(watchedFields.loyaltyPackageId || null);
  const { options: categoryOptions, loading: categoriesLoading } = useLoyaltyCategoryOptions({
    skip: false, // Gọi API ngay khi vào màn hình thay vì đợi nhấn vào mới load
  });
  const { create, loading: createLoading } = useCreateLoyaltyPeriod();
  const { update, loading: updateLoading } = useUpdateLoyaltyPeriod();

  useEffect(() => {
    if (!programDropdownOpen || !programsPage?.loyaltyPackages) return;

    const page = programsPage.loyaltyPackages;
    if (programOffset === 0) {
      setAccumulatedPrograms((prev) => {
        if (prev.length === page.length && page.every((item, index) => prev[index]?.id === item.id)) {
          return prev;
        }
        return page;
      });
      return;
    }

    setAccumulatedPrograms((prev) => {
      const seen = new Set(prev.map((p) => p.id));
      const appended = page.filter((p) => !seen.has(p.id));
      if (!appended.length) return prev;
      return [...prev, ...appended];
    });
  }, [programsPage, programDropdownOpen, programOffset]);

  const programFilterTotal = programsPage?.paginationData?.total ?? 0;
  const hasMorePrograms = accumulatedPrograms.length < programFilterTotal;
  const programsLoadingInitial = programsLoading && programOffset === 0 && accumulatedPrograms.length === 0;
  const programsLoadingMore = programsLoading && programOffset > 0;

  const programOptions = useMemo(() => {
    const programs = accumulatedPrograms
      .filter((p) => [LOYALTY_PACKAGE_STATUS.UPCOMING, LOYALTY_PACKAGE_STATUS.ONGOING].includes(p.status))
      .map((p) => ({ label: p.title, value: p.id, program: p }));

    const selectedId = watchedFields.loyaltyPackageId;
    if (selectedId && selectedProgramFromApi && !programs.some((p) => p.value === selectedId)) {
      return [
        { label: selectedProgramFromApi.title, value: selectedProgramFromApi.id, program: selectedProgramFromApi },
        ...programs,
      ];
    }

    return programs;
  }, [accumulatedPrograms, watchedFields.loyaltyPackageId, selectedProgramFromApi]);

  const { loyaltyPackageId, applyScope, categoryId } = watchedFields;

  const selectedProgram = useMemo(() => {
    if (loyaltyPackageId && selectedProgramFromApi?.id === loyaltyPackageId) {
      return selectedProgramFromApi;
    }

    return programOptions.find((o) => o.value === loyaltyPackageId)?.program;
  }, [programOptions, loyaltyPackageId, selectedProgramFromApi]);

  const periodCategory = editPeriod?.category || sourcePeriod?.category;
  const categoryTreeData = useMemo(() => {
    if (categoryOptions.length > 0) return categoryOptions;
    if (!periodCategory?.id) return categoryOptions;
    return [{ value: periodCategory.id, label: periodCategory.name, disabled: false }];
  }, [categoryOptions, periodCategory]);

  const handleProgramDropdownVisibleChange = useCallback((open) => {
    if (!open) return;
    setProgramDropdownOpen(true);
  }, []);

  const handleProgramPopupScroll = useCallback(
    (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.target;
      const nearBottom = scrollHeight - scrollTop - clientHeight < 24;
      if (nearBottom && hasMorePrograms && !programsLoading) {
        setProgramOffset((prev) => prev + LOYALTY_PROGRAM_FILTER_PAGE_SIZE);
      }
    },
    [hasMorePrograms, programsLoading]
  );

  const handleCategoryDropdownVisibleChange = useCallback(() => {
    // Không cần set state nữa vì API đã được fetch ngay từ đầu
  }, []);

  const applyProgramAutofill = useCallback(
    (programId) => {
      if (isEditMode) return;
      if (isCopyMode && !initialized) return;

      const program = programOptions.find((o) => o.value === programId)?.program;
      if (!program) return;

      const suggestedInterval = suggestTimeIntervalFromProgram(program);
      const effectiveRange = getEffectivePackageDateRangeFromProgram(program);
      const updates = {};

      if (suggestedInterval) updates.timeInterval = suggestedInterval;
      if (effectiveRange) updates.dateRange = toFormDateRangeMoment(effectiveRange);

      if (Object.keys(updates).length > 0) {
        form.setFieldsValue(updates);
      }
    },
    [isEditMode, isCopyMode, initialized, programOptions, form]
  );

  // Tạo mới từ "Chi tiết chương trình": preset CT cha (query ?programId) + autofill 1 lần
  useEffect(() => {
    if (mode !== PACKAGE_FORM_MODE.CREATE || isCopyMode) return;
    if (!presetProgramId || presetProgramApplied) return;

    if (watchedFields.loyaltyPackageId !== presetProgramId) {
      form.setFieldsValue({ loyaltyPackageId: presetProgramId });
      syncWatchedFields({ loyaltyPackageId: presetProgramId });
      return;
    }

    // Đợi chi tiết CT load xong mới autofill thời gian / loại thời gian
    if (!selectedProgramFromApi || selectedProgramFromApi.id !== presetProgramId) return;

    const updates = {};
    const suggestedInterval = suggestTimeIntervalFromProgram(selectedProgramFromApi);
    const effectiveRange = getEffectivePackageDateRangeFromProgram(selectedProgramFromApi);
    if (suggestedInterval) updates.timeInterval = suggestedInterval;
    if (effectiveRange) updates.dateRange = toFormDateRangeMoment(effectiveRange);
    if (Object.keys(updates).length > 0) form.setFieldsValue(updates);

    setPresetProgramApplied(true);
  }, [
    mode,
    isCopyMode,
    presetProgramId,
    presetProgramApplied,
    watchedFields.loyaltyPackageId,
    selectedProgramFromApi,
    form,
    syncWatchedFields,
  ]);

  const handleFormValuesChange = useCallback(
    (_, allValues) => {
      const prevProgramId = watchedFields.loyaltyPackageId;
      bumpFormRevision();
      setWatchedFields({
        loyaltyPackageId: allValues.loyaltyPackageId,
        applyScope: allValues.applyScope,
        categoryId: allValues.categoryId,
      });

      if (allValues.loyaltyPackageId && allValues.loyaltyPackageId !== prevProgramId) {
        applyProgramAutofill(allValues.loyaltyPackageId);
      }
    },
    [bumpFormRevision, watchedFields.loyaltyPackageId, applyProgramAutofill]
  );

  // Clone: pre-fill form khi load xong
  useEffect(() => {
    if (!isCopyMode || !sourcePeriod || initialized) return;

    const periodMilestones = (sourcePeriod.milestones || []).map((m) => ({
      id: m.id,
      minQty: m.minQty,
      rewardUnit: m.rewardUnit,
      rewardAmount: m.rewardAmount,
      displayPercent: m.displayPercent,
      awardAmountTitle: m.awardAmountTitle,
    }));

    const values = mapPeriodToFormValues(sourcePeriod, { clearDates: true });
    form.setFieldsValue({
      ...values,
      dateRange: toFormDateRangeMoment(values.dateRange),
    });
    syncWatchedFields({
      loyaltyPackageId: values.loyaltyPackageId,
      applyScope: values.applyScope,
      categoryId: values.categoryId,
    });
    setMilestones(periodMilestones.length ? periodMilestones : [createDefaultMilestone()]);
    setInitialized(true);
    bumpFormRevision();
  }, [isCopyMode, sourcePeriod, form, initialized, syncWatchedFields, bumpFormRevision]);

  // Edit: pre-fill form khi load xong (giữ dateRange)
  useEffect(() => {
    if (!isEditMode || !editPeriod || initialized || notEditable) return;

    const periodMilestones = (editPeriod.milestones || []).map((m) => ({
      id: m.id,
      minQty: m.minQty,
      rewardUnit: m.rewardUnit,
      rewardAmount: m.rewardAmount,
      displayPercent: m.displayPercent,
      awardAmountTitle: m.awardAmountTitle,
    }));

    const values = mapPeriodToFormValues(editPeriod, { clearDates: false });
    form.setFieldsValue({
      ...values,
      dateRange: toFormDateRangeMoment(values.dateRange),
    });
    syncWatchedFields({
      loyaltyPackageId: values.loyaltyPackageId,
      applyScope: values.applyScope,
      categoryId: values.categoryId,
    });
    setMilestones(periodMilestones.length ? periodMilestones : [createDefaultMilestone()]);
    setInitialized(true);
    bumpFormRevision();
  }, [isEditMode, editPeriod, form, initialized, notEditable, syncWatchedFields, bumpFormRevision]);

  // Clone: pre-fill products khi load xong
  useEffect(() => {
    if (!isCopyMode || productsInitialized) return;

    const periodProducts = sourcePeriod?.periodProducts;
    if (!periodProducts) return;

    const periodMilestones = sourcePeriod?.milestones || milestones;
    setProducts(mapPeriodProductsToForm(periodProducts, periodMilestones));
    setProductsInitialized(true);
  }, [isCopyMode, sourcePeriod, milestones, productsInitialized]);

  // Edit: pre-fill products khi load xong
  useEffect(() => {
    if (!isEditMode || notEditable || productsInitialized) return;

    const periodProducts = editPeriod?.periodProducts;
    if (!periodProducts) return;

    const periodMilestones = editPeriod?.milestones || milestones;
    setProducts(mapPeriodProductsToForm(periodProducts, periodMilestones));
    setProductsInitialized(true);
  }, [isEditMode, notEditable, editPeriod, milestones, productsInitialized]);

  // Edit: snapshot baseline sau khi pre-fill xong — dùng so sánh "Lưu lại" không đổi
  useEffect(() => {
    if (!isEditMode || !initialized || !productsInitialized || editBaseline) return;

    setEditBaseline(
      serializePackageFormForCompare({
        formValues: form.getFieldsValue(),
        milestones,
        products,
      })
    );
  }, [isEditMode, initialized, productsInitialized, editBaseline, form, milestones, products]);

  const handleMilestoneChange = useCallback((index, field, value) => {
    setMilestones((prev) => prev.map((m, i) => (i === index ? { ...m, [field]: value } : m)));
    setMilestoneErrors((prev) => {
      if (!prev[index]?.[field]) return prev;
      const next = prev.map((e) => ({ ...e }));
      delete next[index][field];
      return next;
    });
  }, []);

  const handleAddMilestone = useCallback((afterIndex) => {
    setMilestones((prev) => {
      const insertAt = afterIndex + 1;
      const next = [...prev];
      next.splice(insertAt, 0, createDefaultMilestone());
      return next;
    });
    setMilestoneErrors([]);
  }, []);

  const handleRemoveMilestone = useCallback((index) => {
    setMilestoneErrors([]);
    setMilestones((prev) => {
      const removed = prev[index];
      const next = prev.filter((_, i) => i !== index);
      if (next.length === 0) return [createDefaultMilestone()];

      setProducts((prods) =>
        prods.map((p) => {
          if (!removed?.id || !p.milestoneConfigs?.[removed.id]) return p;
          const { [removed.id]: _, ...rest } = p.milestoneConfigs;
          return { ...p, milestoneConfigs: rest };
        })
      );

      return next;
    });
  }, []);

  const handleApplyScopeChange = useCallback(
    (newScope) => {
      const currentScope = getEffectiveApplyScope(applyScope);

      if (
        newScope === LOYALTY_APPLY_SCOPE.SINGLE_PRODUCT &&
        currentScope !== LOYALTY_APPLY_SCOPE.SINGLE_PRODUCT &&
        products.length > 1
      ) {
        return { needConfirm: true, newScope };
      }

      syncWatchedFields({ applyScope: newScope });
      // Message lỗi sản phẩm khác nhau theo từng applyScope — nếu đang hiện lỗi thì re-validate theo scope mới
      // luôn, tránh treo message cũ (vd "chỉ chọn 1 sản phẩm") sau khi đã đổi sang scope khác.
      setProductError((prev) => (prev ? validatePackageProducts({ applyScope: newScope, products }).message : prev));
      return { needConfirm: false, newScope };
    },
    [applyScope, products, syncWatchedFields]
  );

  const confirmApplyScopeChange = useCallback(
    (newScope) => {
      form.setFieldsValue({ applyScope: newScope });
      syncWatchedFields({ applyScope: newScope });
      setProducts([]);
      setProductError((prev) =>
        prev ? validatePackageProducts({ applyScope: newScope, products: [] }).message : null
      );
      bumpFormRevision();
    },
    [form, syncWatchedFields, bumpFormRevision]
  );

  const handleCategoryChangeAttempt = useCallback(
    (newCategoryId) => {
      if (categoryId && newCategoryId !== categoryId && (products.length > 0 || hasConfiguredMilestones(milestones))) {
        return true;
      }
      return false;
    },
    [categoryId, products.length, milestones]
  );

  const confirmCategoryChange = useCallback(
    (newCategoryId) => {
      form.setFieldsValue({ categoryId: newCategoryId });
      syncWatchedFields({ categoryId: newCategoryId });
      setProducts([]);
      setProductError(null);
      setMilestones([createDefaultMilestone()]);
      bumpFormRevision();
    },
    [form, syncWatchedFields, bumpFormRevision]
  );

  const handleAddProducts = useCallback((selectedProducts) => {
    setProductError(null);
    setProducts((prev) => {
      const existingIds = new Set(prev.map((p) => p.productId));
      const toAdd = selectedProducts
        .filter((p) => !existingIds.has(p.id))
        .map((p) => ({
          productId: p.id,
          productName: p.name,
          productCode: p.code,
          milestoneConfigs: {},
        }));
      return [...prev, ...toAdd];
    });
  }, []);

  const handleRemoveProduct = useCallback((productId) => {
    setProductError(null);
    setProducts((prev) => prev.filter((p) => p.productId !== productId));
  }, []);

  const handleUpdateProductMilestones = useCallback((productId, configs) => {
    setProducts((prev) => prev.map((p) => (p.productId === productId ? { ...p, milestoneConfigs: configs } : p)));
  }, []);

  const scrollToPackageFormError = useCallback((selector = ".package-form .ant-form-item-has-error") => {
    requestAnimationFrame(() => {
      document.querySelector(selector)?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, []);

  const scrollToProductError = useCallback(() => {
    requestAnimationFrame(() => {
      document
        .querySelector(".package-form-product-section .package-form-product-error")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, []);

  const scrollToMilestoneError = useCallback(() => {
    requestAnimationFrame(() => {
      document
        .querySelector(".package-form-milestone-section .package-form-milestone-error")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, []);

  const handleFormSubmitFailed = useCallback(() => {
    // antd tự hiển thị warning inline cho các field bắt buộc của section "Thông tin chung".
    // Đồng thời surface warning inline cho section "Cột mốc" + "Sản phẩm" để hiện cùng lúc.
    setMilestoneErrors(validateMilestoneFields(milestones).errors);
    setProductError(validatePackageProducts({ applyScope, products }).message);
    scrollToPackageFormError();
  }, [milestones, applyScope, products, scrollToPackageFormError]);

  const handleFormSubmit = useCallback(
    (values) => {
      // Section "Cột mốc nhận chiết khấu" — warning inline dưới từng field, không toast.
      const milestoneCheck = validateMilestoneFields(milestones);
      setMilestoneErrors(milestoneCheck.errors);

      // Section "Thông tin sản phẩm hưởng chiết khấu" — warning inline dưới bảng, không toast.
      // Set đồng thời với milestone (không return sớm ở trên) để cả 2 section cùng hiện lỗi một lượt.
      const productCheck = validatePackageProducts({ applyScope: values.applyScope, products });
      setProductError(productCheck.message);

      if (!milestoneCheck.valid) {
        scrollToMilestoneError();
        return;
      }
      if (!productCheck.valid) {
        scrollToProductError();
        return;
      }

      // Các rule nghiệp vụ còn lại (thời gian nằm trong CT cha...) — giữ toast.
      const validation = validatePackageForm({
        formValues: values,
        milestones,
        products,
        selectedProgram,
      });

      if (!validation.valid) {
        notify.error({ message: validation.message });
        scrollToPackageFormError();
        return;
      }

      setPendingFormValues(values);
      setConfirmVisible(true);
    },
    [milestones, products, selectedProgram, scrollToPackageFormError, scrollToMilestoneError, scrollToProductError]
  );

  const exitForm = useCallback(() => {
    if (history.length > 1) {
      history.goBack();
      return;
    }

    if (isEditMode && periodId) {
      history.push(`/loyalty-package/package/${periodId}`);
      return;
    }

    history.push(presetProgramId ? `/loyalty-package/program/${presetProgramId}` : "/loyalty-package?tab=package");
  }, [history, isEditMode, periodId, presetProgramId]);

  const handleConfirmSubmit = useCallback(async () => {
    if (!pendingFormValues) return;

    try {
      const input = buildPeriodInput({
        formValues: pendingFormValues,
        milestones,
        products,
      });

      if (isEditMode) {
        await update(periodId, input);
        notify.success({ message: t("loyaltyPackage.package.updateSuccess") });
        setConfirmVisible(false);
        // Quay về đúng màn đã mở form sửa (Chi tiết gói hoặc List) thay vì luôn về List
        exitForm();
        return;
      }

      await create(input);
      notify.success({ message: t("loyaltyPackage.package.createSuccess") });
      setConfirmVisible(false);
      // Thêm mới/clone: quay về đúng màn đã mở form (Chi tiết chương trình hoặc tab Gói)
      exitForm();
    } catch (err) {
      const fallbackKey = isEditMode ? "loyaltyPackage.package.updateFailed" : "loyaltyPackage.package.createFailed";
      notify.error({ message: err?.message || t(fallbackKey) });
    }
  }, [pendingFormValues, milestones, products, isEditMode, periodId, update, create, t, exitForm]);

  const isEditDirty = useMemo(() => {
    if (!isEditMode || !editBaseline) return false;

    return (
      serializePackageFormForCompare({
        formValues: form.getFieldsValue(),
        milestones,
        products,
      }) !== editBaseline
    );
    // formRevision: antd bản cũ không có Form.useWatch — recompute khi form/mốc/SP đổi
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, editBaseline, formRevision, milestones, products, form]);

  const isCreateDirty = useMemo(() => {
    if (isEditMode || isCopyMode) return false;

    const values = form.getFieldsValue();
    if (values.title?.trim()) return true;
    if (values.dateRange?.[0] && values.dateRange?.[1]) return true;
    if (values.categoryId || values.loyaltyPackageId || values.timeInterval) return true;
    if (values.applyScope && values.applyScope !== LOYALTY_APPLY_SCOPE.MULTIPLE_PRODUCTS) return true;
    if (products.length > 0) return true;
    if (milestones.length > 1) return true;

    return milestones.some(
      (milestone) => milestone.minQty != null || milestone.rewardAmount != null || milestone.displayPercent != null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, isCopyMode, formRevision, milestones, products, form]);

  const hasUnsavedChanges = isEditMode ? isEditDirty : isCopyMode ? initialized : isCreateDirty;

  const saveDisabled = isEditMode && Boolean(editBaseline) && !isEditDirty;

  const handleCancel = useCallback(() => {
    if (!hasUnsavedChanges) {
      exitForm();
      return;
    }
    setCancelVisible(true);
  }, [hasUnsavedChanges, exitForm]);

  const confirmCancel = useCallback(() => {
    setCancelVisible(false);
    exitForm();
  }, [exitForm]);

  const pageLoading = (isCopyMode && sourceLoading) || (isEditMode && editLoading);

  const submitting = isEditMode ? updateLoading : createLoading;

  return {
    form,
    mode,
    isEditMode,
    periodId,
    notEditable,
    isCopyMode,
    pageLoading,
    milestones,
    milestoneErrors,
    products,
    productError,
    programOptions,
    categoryOptions: categoryTreeData,
    categoriesLoading,
    programsLoading: programsLoadingInitial,
    programsLoadingMore,
    handleProgramDropdownVisibleChange,
    handleProgramPopupScroll,
    handleCategoryDropdownVisibleChange,
    applyScope,
    categoryId,
    selectedProgram,
    timeIntervalOptions: TIME_INTERVAL_OPTIONS,
    confirmVisible,
    cancelVisible,
    pendingFormValues,
    submitting,
    saveDisabled,
    isEditDirty,
    handleMilestoneChange,
    handleAddMilestone,
    handleRemoveMilestone,
    handleApplyScopeChange,
    confirmApplyScopeChange,
    handleCategoryChangeAttempt,
    confirmCategoryChange,
    handleAddProducts,
    handleRemoveProduct,
    handleUpdateProductMilestones,
    handleFormSubmit,
    handleFormSubmitFailed,
    handleConfirmSubmit,
    handleCancel,
    confirmCancel,
    handleFormValuesChange,
    setConfirmVisible,
    setCancelVisible,
  };
}

export default usePackageForm;
